import { NbfcLink } from "./NbfcLink";
import { FiShield as FeatherShield, FiMail, FiMapPin, FiPhone } from "react-icons/fi";
import brandLogo from "../assets/brandLogo.png"
import SecurityNotice from "./SecurityNotice";

const Footer = ({ showSecurityNotice = true }: { showSecurityNotice?: boolean }) => {
  const currentYear = new Date().getFullYear();
  const brandName = import.meta.env.VITE_BRAND_NAME;
  const twitterUrl = import.meta.env.VITE_COMPANY_TWITTER_URL;
  const linkedinUrl = import.meta.env.VITE_COMPANY_LINKEDIN_URL;
  const companyPhone = import.meta.env.VITE_COMPANY_PHONE;
  const rbiRegulated = "RBI Regulated NBFC Partner";

  return (
    <>
      {showSecurityNotice && <SecurityNotice />}
      <footer className="w-full bg-[#f4f7f9] text-[var(--text-gray)] py-16 px-4 mt-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div className="space-y-6">
              <img
                src={brandLogo}
                alt={`${import.meta.env.VITE_COMPANY_NAME} logo`}
                className="h-10 md:h-12 object-contain"
              />
              <p className="text-[12px] font-semibold tracking-widest leading-[1.6] text-gray-400 opacity-80 uppercase max-w-[240px]">
                Fast, Easy, and Paperless Personal Loans with Instant Approval and Bank-Grade Security.
              </p>
            </div>

            {/* Company Column */}
            <div className="pt-2">
              <h4 className="text-[12px] font-semibold tracking-widest text-[#7D8FA9] uppercase mb-8">Company</h4>
              <ul className="space-y-4 font-semibold text-[13px] opacity-80 text-[#475569]">
                {/* <li><a href="#" className="hover:text-[var(--text-primary)] transition-colors">RBI Compliance</a></li> */}
                <li><a href="/privacy-policy" className="hover:text-[var(--text-primary)] transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-condition" className="hover:text-[var(--text-primary)] transition-colors">Terms of Service</a></li>
                <li><a href="/fair-practice-code" className="hover:text-[var(--text-primary)] transition-colors">Fair Practice Code</a></li>
                <li><a href="/corporate-governance" className="hover:text-[var(--text-primary)] transition-colors">Corporate Governance</a></li>
                <li><a href="/account-delete-policy" className="hover:text-[var(--text-primary)] transition-colors">Account Deletion Policy</a></li>
                <li><a href="/interest-rate-policy" className="hover:text-[var(--text-primary)] transition-colors">Interest Rate Policy</a></li>
                <li><a href="/kyc-aml-policy" className="hover:text-[var(--text-primary)] transition-colors">KYC & AML Policy</a></li>
                <li><a href="/risk-management-policy" className="hover:text-[var(--text-primary)] transition-colors">Risk Management Policy</a></li>
                <li><a href="/cancellation-refund-policy" className="hover:text-[var(--text-primary)] transition-colors">Cancellation & Refund Policy</a></li>
                <li><a href="https://rbi.org.in/" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-primary)] transition-colors">RBI Portal</a></li>
                <li><a href="https://sachet.rbi.org.in/" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-primary)] transition-colors">Sachet Portal</a></li>
              </ul>
            </div>

            {/* Social Column */}
            <div className="pt-2">
              <h4 className="text-[12px] font-semibold tracking-widest text-[#7D8FA9] uppercase mb-8">Social</h4>
              <ul className="space-y-5 font-semibold text-[15px] opacity-80 text-[#475569]">
                <li><a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-primary)] transition-colors">Twitter</a></li>
                <li><a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-primary)] transition-colors">LinkedIn</a></li>
                <li><a href="#" className="hover:text-[var(--text-primary)] transition-colors">Contact us</a></li>
              </ul>
            </div>

            {/* Partners Column */}
            <div className="pt-2">
              <h4 className="text-[12px] font-semibold tracking-widest text-[#7D8FA9] uppercase mb-8">Partners</h4>

              {/* Contact Info List */}
              <ul className="space-y-4 font-semibold text-[#475569] mb-8">
                {companyPhone && (
                  <li className="flex items-center gap-3">
                    <FiPhone className="text-[var(--primary)] shrink-0" />
                    <a href={`tel:${companyPhone}`} className="hover:text-[var(--text-primary)] transition-colors text-[13px]">
                      {companyPhone}
                    </a>
                  </li>
                )}
                <li className="flex items-center gap-3">
                  <FiMail className="text-[var(--primary)] shrink-0" />
                  <a href={`mailto:${import.meta.env.VITE_COMPANY_EMAIL}`} className="hover:text-[var(--text-primary)] transition-colors text-[13px]">
                    {import.meta.env.VITE_COMPANY_EMAIL}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <FiMapPin className="text-[var(--primary)] mt-1 shrink-0" />
                  <span className="text-[11px] leading-relaxed max-w-[200px]">
                    {import.meta.env.VITE_COMPANY_ADDRESS}
                  </span>
                </li>
              </ul>

              <div className="bg-[#E7ECF3] rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase mb-2">NBFC Partner</p>
                <p className="font-semibold text-black text-[15px] leading-tight">
                  <NbfcLink />
                </p>

                {/* RBI License Badge */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/50 border border-white/80 shadow-sm mt-4 inline-flex">
                  <FeatherShield className="text-[var(--primary)] text-sm" />
                  <span className="text-[10px] font-black text-[var(--text-primary)] uppercase tracking-tight">
                    RBI NBFC License: <span className="text-[var(--primary)]">{import.meta.env.VITE_COMPANY_NBFC_LICENSE}</span>
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Copyright Section */}
          <div className="pt-10 border-t border-gray-200/60 text-center">
            <p className="text-[11px] font-semibold text-gray-400 opacity-70 tracking-tight">
              © {currentYear} {brandName}. {rbiRegulated}. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;

